import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CustomCaseProperty,
  CustomPropertyCategory,
  CustomPropertyType,
  DeleteCustomPropertyDocument,
} from "generated/graphql";
import { useContext, useMemo, useState } from "react";
import { EllipsisSX, LinkSX } from "theme";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate } from "react-router";
import { useAppSelector } from "redux/hooks";
import {
  fetchCustomProperties,
  selectCaseDisplayGroups,
  selectCaseProperties,
  selectLensEditorLens,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";
import CopyToClipboard from "components/CopyToClipboard";
import {
  ClipboardCaseProperty,
  compareCustomProperty,
  makeCasePropertyForCopy,
  onPropertyChecked,
} from "./ImportUtils";
import { ImportCasePropertiesDialog } from "./ImportCasePropertiesDialog";
import { BulkDeletePropertiesDialog } from "./ImportBulkDeletePropertiesDialog";
import CheckAll from "routes/lenses/lens/files/CheckAll";
import CasePropertyDialog from "./CasePropertyDialog";
import PSelect from "components/PSelect";
import { format, parseISO } from "date-fns";
import DisplayGroupsCell from "./DisplayGroupsCell";
import { empty } from "helpers/coll";
import DeleteIcon from "@mui/icons-material/Delete";
import { runGqlMutation } from "gqlClient";
import store from "redux/store";
import { useSnackbar } from "notistack";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import PublishCustomPropertiesModal from "routes/lens-editor/PublishCustomPropertiesModal";

export function CustomCaseProperties() {
  const { lensId } = useParamNumbers(["lensId"]);
  const lens = useAppSelector(selectLensEditorLens);
  const [showPublishCustomPropertiesModal, setShowPublishCustomPropertiesModal] = useState(false);
  const [search, setSearch] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const caseProperties = useAppSelector(selectCaseProperties);
  const filteredCaseProperties = caseProperties.filter((cp) => {
    if (!search) return cp;
    else {
      return (
        cp.key.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1 ||
        cp.displayName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1
      );
    }
  });
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const [selectedCaseProperties, setSelectedCaseProperties] = useState<CustomCaseProperty[]>([]);
  const [editingCaseProperty, setEditingCaseProperty] = useState<CustomCaseProperty | undefined>(undefined);
  const [newCaseProperty, setNewCaseProperty] = useState(false);
  const propertiesForCopy: ClipboardCaseProperty[] = useMemo(
    () => selectedCaseProperties.map((prop) => makeCasePropertyForCopy(prop)),
    [selectedCaseProperties]
  );
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirmCtx = useContext(ConfirmationDialogContext);

  if (lensId === undefined) {
    return <Navigate to=".." />;
  }

  const deleteProperty = (key: string) => {
    confirmCtx.confirm({
      title: "Delete Case Property",
      prompt: (
        <div className="flex flex-col gap-4">
          <Alert severity="warning">
            This will remove the values for <i>{key}</i> on <strong>every case</strong>
          </Alert>
          <p className="text-sm font-extrabold">Delete Case Property &apos;{key}&apos;</p>
        </div>
      ),
      callback: async () => {
        return runGqlMutation(DeleteCustomPropertyDocument, {
          lensId: lensId,
          key: key,
          customPropertyCategory: CustomPropertyCategory.CaseCustomProperty,
        })
          .then(() => store.dispatch(fetchCustomProperties({ lensId })))
          .then(() => {
            enqueueSnackbar(`Successfully deleted '${key}'`, {
              variant: "success",
            });
          })
          .catch(() =>
            enqueueSnackbar(`Failed to delete '${key}'`, {
              variant: "error",
            })
          );
      },
    });
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 2 }}>
        <CopyToClipboard
          disabled={empty(propertiesForCopy)}
          text={JSON.stringify(propertiesForCopy, null, 2)}
          title="Copy Selected Properties for Import"
        />
        <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Box sx={{ flexGrow: 1 }} />
        {lens.isFamilyPrimary && (
          <Button variant="outlined" onClick={() => setShowPublishCustomPropertiesModal(true)}>
            Publish to Lens Family
          </Button>
        )}
        {showPublishCustomPropertiesModal && (
          <PublishCustomPropertiesModal
            lensId={lensId}
            customPropertyCategory={CustomPropertyCategory.CaseCustomProperty}
            open={true}
            close={() => setShowPublishCustomPropertiesModal(false)}
          />
        )}
        {selectedCaseProperties.length > 1 && (
          <Button variant="contained" color="error" className="text-white" onClick={() => setOpenBulkDelete(true)}>
            Bulk Delete ({selectedCaseProperties.length})
          </Button>
        )}
        <Button variant="outlined" onClick={() => setShowImportDialog(true)}>
          Import
        </Button>
        <Button variant="contained" onClick={() => setNewCaseProperty(true)}>
          Add Property
        </Button>
      </Box>

      <Box sx={{ overflow: "auto", minHeight: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 0 }}>
                <CheckAll<CustomCaseProperty>
                  selectedItems={selectedCaseProperties}
                  items={caseProperties}
                  setItems={setSelectedCaseProperties}
                  compareFn={compareCustomProperty}
                />
              </TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Display Groups</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell>Collect</TableCell>
              <TableCell>Data Type</TableCell>
              <TableCell>Select Options</TableCell>
              <TableCell>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCaseProperties.map((prop) => (
              <TableRow key={prop.key}>
                <TableCell sx={{ px: 0 }}>
                  <Checkbox
                    checked={!!selectedCaseProperties.find((sc) => compareCustomProperty(sc, prop))}
                    onChange={() => setSelectedCaseProperties(onPropertyChecked(selectedCaseProperties, prop))}
                    size="small"
                    color="secondary"
                  />
                </TableCell>
                <TableCell sx={LinkSX} onClick={() => setEditingCaseProperty(prop)}>
                  {prop.displayName}
                </TableCell>
                <TableCell>
                  <P variant="block12">{prop.key}</P>
                </TableCell>
                <DisplayGroupsCell propertyKey={prop.key} displayGroups={caseDisplayGroups} />
                <TableCell
                  sx={{
                    maxWidth: 200,
                  }}>
                  <Tooltip title={prop.description}>
                    <P sx={EllipsisSX}> {prop.description}</P>
                  </Tooltip>
                </TableCell>
                <TableCell>{prop.isHidden ? "No" : "Yes"}</TableCell>
                <TableCell>{prop.collectVisibility}</TableCell>
                <TableCell>{prop.dataType}</TableCell>
                <TableCell>
                  {prop.dataType === CustomPropertyType.Select ? (
                    <PSelect defaultValue={prop.selectOptions[0]}>
                      {prop.selectOptions.map((o) => (
                        <MenuItem value={o} key={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </PSelect>
                  ) : (
                    <></>
                  )}
                </TableCell>
                <TableCell>{format(parseISO(prop.createdAt), "MM/dd/yyyy")}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => deleteProperty(prop.key)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {newCaseProperty && (
        <CasePropertyDialog lensId={lensId} open={newCaseProperty} close={() => setNewCaseProperty(false)} />
      )}
      {editingCaseProperty && (
        <CasePropertyDialog
          lensId={lensId}
          customProp={editingCaseProperty}
          open={true}
          close={() => {
            setEditingCaseProperty(undefined);
          }}
        />
      )}
      {showImportDialog && <ImportCasePropertiesDialog onClose={() => setShowImportDialog(false)} lensId={lensId} />}
      <BulkDeletePropertiesDialog<CustomCaseProperty>
        open={openBulkDelete}
        onClose={() => setOpenBulkDelete(false)}
        lensId={lensId}
        propertyKeys={selectedCaseProperties.map((s) => s.key)}
        propertyType={CustomPropertyCategory.CaseCustomProperty}
        setSelected={setSelectedCaseProperties}
      />
    </Card>
  );
}
